'use client'
import React from 'react'
import styled from 'styled-components'
import CheckIcon from '@mui/icons-material/Check'
import SchoolIcon from '@mui/icons-material/School'
import { useConfig } from '@/contexts/ConfigContext'
import { useLanguage } from '@/contexts/LanguageContext'
import ModuleContainer from './ModuleContainer'
import { Tooltip } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import KeySelector from '@components/CustomInput/KeySelector'

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    transition: opacity 0.2s ease;
`

const ControlRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    color: #ccc;
    font-size: 0.9rem;
    min-height: 42px;
    padding: 10px 15px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.03);
    transition: all 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(99, 102, 241, 0.2);
    }
`

const Checkbox = styled.div`
    width: 22px;
    height: 22px;
    border: 2px solid #666;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    background: ${({ checked }) => (checked ? '#6366f1' : 'transparent')};
    border-color: ${({ checked }) => (checked ? '#6366f1' : '#666')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${({ checked }) => (checked ? '0 0 10px rgba(99, 102, 241, 0.3)' : 'none')};
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: #6366f1;
    }
`

const ColumnsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 30px;
    align-items: stretch;
    width: 100%;
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    min-width: 320px;
`

const SectionTitle = styled.h4`
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: #f163cdff;
    font-weight: 600;
    font-family: var(--font-chakra);
    margin-bottom: 5px;
    display: flex;
    align-items: center;
    gap: 12px;

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(99, 102, 241, 0.4), transparent);
    }
`

const BindGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: rgba(0, 0, 0, 0.25);
    padding: 18px;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    box-shadow: inset 0 0 20px rgba(0, 0, 0, 0.2);
    width: 100%;
`

const BindItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    width: 100%;

    &:last-child {
        padding-bottom: 0;
        border-bottom: none;
    }
`

const BindLabel = ({ title, desc, help }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#e2e8f0', fontFamily: 'var(--font-roboto)' }}>
                {title}
            </span>
            {help && (
                <Tooltip title={help} placement="top" arrow>
                    <HelpOutlineIcon style={{ fontSize: 13, opacity: 0.25, cursor: 'help' }} />
                </Tooltip>
            )}
        </div>
        {desc && <span style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.35)', lineHeight: 1.3 }}>{desc}</span>}
    </div>
)

const TipBox = styled.div`
    display: flex;
    gap: 15px;
    padding: 15px 20px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1));
    border-radius: 10px;
    border: 1px solid rgba(99, 102, 241, 0.2);
    margin-top: 10px;

    .icon {
        font-size: 1.2rem;
        flex-shrink: 0;
    }

    .content {
        font-size: 0.75rem;
        color: #a5b4fc;
        line-height: 1.6;
        
        strong {
            color: #fff;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        code {
            background: rgba(255, 255, 255, 0.1);
            padding: 2px 6px;
            border-radius: 4px;
            color: #fff;
            font-weight: 800;
            margin: 0 2px;
        }
    }
`

const CheckboxOption = ({ label, description, checked, onChange, disabled }) => (
    <ControlRow style={{ opacity: disabled ? 0.5 : 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#f8fafc', fontFamily: 'var(--font-roboto)' }}>
                    {label}
                </span>
                {description && (
                    <Tooltip title={description} placement="right" arrow>
                        <HelpOutlineIcon style={{ fontSize: 14, opacity: 0.3, cursor: 'help' }} />
                    </Tooltip>
                )}
            </div>
        </div>
        <Checkbox checked={checked} onClick={() => !disabled && onChange(!checked)} disabled={disabled}>
            {checked && <CheckIcon style={{ fontSize: 14, color: 'white' }} />}
        </Checkbox>
    </ControlRow>
)

const FullWidthSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-bottom: 5px;
`

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 25px;
    width: 100%;
`

const HorizontalOptions = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    width: 100%;
`

export default function PracticeModule({ id, initialExpanded, isDemoBlocked }) {
    const { t } = useLanguage()
    const { activeModules, configData, updateConfig, configName, configIsLocked } = useConfig()

    const isActive = activeModules.includes('practice')

    const handleChange = (key, value) => {
        updateConfig('practice', key, value)
    }

    return (
        <ModuleContainer
            id={id}
            moduleKey="practice"
            title={t('PRACTICE_TITLE')}
            icon={SchoolIcon}
            description={t('PRACTICE_DESC')}
            initialExpanded={initialExpanded}
            isComingSoon={true}
            isDemoBlocked={isDemoBlocked}
        >
            <Controls isActive={isActive}>
                <TipBox style={{ marginTop: 0, alignItems: 'center' }}>
                    <div className="icon">💡</div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', paddingRight: '20px', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                        <KeySelector
                            value={configData.practice.bind_prac_alias}
                            onChange={(val) => handleChange('bind_prac_alias', val)} fieldPath="practice.bind_prac_alias"
                            disabled={configIsLocked}
                        />
                    </div>
                    <div className="content" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {t('PRAC_TIP')}
                    </div>
                </TipBox>

                <FullWidthSection>
                    <SectionTitle>{t('SERVER_OPTIMIZATION')}</SectionTitle>
                    <HorizontalOptions>
                        <CheckboxOption
                            label={t('CHEAT_MODE_LABEL')}
                            description="sv_cheats 1"
                            checked={configData.practice.sv_cheats}
                            onChange={(val) => handleChange('sv_cheats', val)} fieldPath="practice.sv_cheats"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('INFINITE_RESOURCES')}
                            description={t('INFINITE_RESOURCES_DESC_HELP')}
                            checked={configData.practice.infinite_resources}
                            onChange={(val) => handleChange('infinite_resources', val)} fieldPath="practice.infinite_resources"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('GRENADE_UTILS')}
                            description={t('GRENADE_UTILS_DESC_HELP')}
                            checked={configData.practice.grenade_utils}
                            onChange={(val) => handleChange('grenade_utils', val)} fieldPath="practice.grenade_utils"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('AUTO_SETUP')}
                            description={t('AUTO_SETUP_DESC_HELP')}
                            checked={configData.practice.server_optimization}
                            onChange={(val) => handleChange('server_optimization', val)} fieldPath="practice.server_optimization"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('BOT_FREEZE')}
                            description={t('BOT_FREEZE_DESC_HELP')}
                            checked={configData.practice.bot_freeze}
                            onChange={(val) => handleChange('bot_freeze', val)} fieldPath="practice.bot_freeze"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('AUTO_HEAL')}
                            description={t('AUTO_HEAL_DESC_HELP')}
                            checked={configData.practice.auto_heal}
                            onChange={(val) => handleChange('auto_heal', val)} fieldPath="practice.auto_heal"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('RADAR_PRACTICE')}
                            description={t('RADAR_PRACTICE_DESC_HELP')}
                            checked={configData.practice.radar_full}
                            onChange={(val) => handleChange('radar_full', val)} fieldPath="practice.radar_full"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('WALLBANG_DATA')}
                            description={t('WALLBANG_DATA_DESC_HELP')}
                            checked={configData.practice.wallbang_info}
                            onChange={(val) => handleChange('wallbang_info', val)} fieldPath="practice.wallbang_info"
                            disabled={configIsLocked}
                        />

                        <CheckboxOption
                            label={t('AUTO_BHOP')}
                            description={t('AUTO_BHOP_DESC_HELP')}
                            checked={configData.practice.auto_bhop}
                            onChange={(val) => handleChange('auto_bhop', val)} fieldPath="practice.auto_bhop"
                            disabled={configIsLocked}
                        />
                    </HorizontalOptions>
                </FullWidthSection>

                <GridContainer>
                    {/* Binds Section */}
                    <Column>
                        <SectionTitle>{t('BINDS_PRACTICE_TITLE')}</SectionTitle>

                        <BindGroup>
                            <BindItem>
                                <BindLabel
                                    title={t('BIND_NOCLIP')}
                                    desc={t('NOCLIP_DESC')}
                                    help={t('NOCLIP_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_noclip}
                                    onChange={(val) => handleChange('bind_noclip', val)} fieldPath="practice.bind_noclip"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_CLEAR_UTIL')}
                                    desc={t('BIND_CLEAR_UTIL_DESC')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_clear_util}
                                    onChange={(val) => handleChange('bind_clear_util', val)} fieldPath="practice.bind_clear_util"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_TOGGLE_IMPACTS')}
                                    desc={t('BIND_TOGGLE_IMPACTS_DESC')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_toggle_impacts}
                                    onChange={(val) => handleChange('bind_toggle_impacts', val)} fieldPath="practice.bind_toggle_impacts"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_RETHROW')}
                                    desc={t('RETHROW_DESC')}
                                    help={t('RETHROW_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_rethrow}
                                    onChange={(val) => handleChange('bind_rethrow', val)} fieldPath="practice.bind_rethrow"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_TIMESCALE')}
                                    desc={t('TIMESCALE_DESC')}
                                    help={t('TIMESCALE_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_timescale}
                                    onChange={(val) => handleChange('bind_timescale', val)} fieldPath="practice.bind_timescale"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_RESTART_ROUND')}
                                    desc={t('RESTART_ROUND_DESC')}
                                    help={t('RESTART_ROUND_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_restart_round}
                                    onChange={(val) => handleChange('bind_restart_round', val)} fieldPath="practice.bind_restart_round"
                                    disabled={configIsLocked}
                                />
                            </BindItem>
                        </BindGroup>
                    </Column>

                    {/* Checkpoints Section */}
                    <Column>
                        <SectionTitle>{t('CHECKPOINTS_BOTS')}</SectionTitle>
                        <BindGroup>
                            <BindItem>
                                <BindLabel
                                    title={t('SAVE_POSITION')}
                                    desc={t('SAVE_POS_DESC')}
                                    help={t('SAVE_POS_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_save_pos}
                                    onChange={(val) => handleChange('bind_save_pos', val)} fieldPath="practice.bind_save_pos"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('LOAD_POSITION')}
                                    desc={t('LOAD_POS_DESC')}
                                    help={t('LOAD_POS_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_load_pos}
                                    onChange={(val) => handleChange('bind_load_pos', val)} fieldPath="practice.bind_load_pos"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_BOT_PLACE')}
                                    desc={t('BOT_PLACE_DESC')}
                                    help={t('BOT_PLACE_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_bot_place}
                                    onChange={(val) => handleChange('bind_bot_place', val)} fieldPath="practice.bind_bot_place"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem style={{ flex: 1, paddingBottom: 0, borderBottom: 'none' }}>
                                <BindLabel title={t('ADD_BOT_T')} />
                                <KeySelector
                                    value={configData.practice.bind_bot_add_t}
                                    onChange={(val) => handleChange('bind_bot_add_t', val)} fieldPath="practice.bind_bot_add_t"
                                    disabled={configIsLocked}
                                />
                            </BindItem>
                            <BindItem style={{ flex: 1, paddingBottom: 0, borderBottom: 'none' }}>
                                <BindLabel title={t('ADD_BOT_CT')} />
                                <KeySelector
                                    value={configData.practice.bind_bot_add_ct}
                                    onChange={(val) => handleChange('bind_bot_add_ct', val)} fieldPath="practice.bind_bot_add_ct"
                                    disabled={configIsLocked}
                                />
                            </BindItem>

                            <BindItem>
                                <BindLabel
                                    title={t('BIND_BOT_KICK')}
                                    help={t('BOT_KICK_HELP')}
                                />
                                <KeySelector
                                    value={configData.practice.bind_bot_kick}
                                    onChange={(val) => handleChange('bind_bot_kick', val)} fieldPath="practice.bind_bot_kick"
                                    disabled={configIsLocked}
                                />
                            </BindItem>
                        </BindGroup>
                    </Column>
                </GridContainer>
            </Controls>
        </ModuleContainer>
    )
}
